// ABC в динамике: как сдвинулись блюда между периодом сравнения и текущим (оба — buildMenuAbc с тем же выбором
// ресторана и категории). Блюда сопоставляются по ключу (ID iiko): переименованное — то же блюдо.
// Сдвиг — по букве прибыли (основной критерий). Заметный: через букву (A ↔ C) — всегда; на одну букву — только если
// прибыль блюда изменилась на NOTABLE_PROFIT_CHANGE и больше (иначе блюда на границе групп прыгали бы каждый период).
// Новое — продаж в прошлом периоде не было; пропавшее — были, а в текущем нет (выведено, в стопе или переименовано без ID).

export const NOTABLE_PROFIT_CHANGE = 0.2;
const RANK = { A: 0, B: 1, C: 2 };
const MINUS = '−';

// Относительное изменение: (стало − было) / |было|; было 0 — null (процент не имеет смысла)
export function relativeChange(current, previous) {
  const prev = Number(previous) || 0;
  if (prev === 0) return null;
  return ((Number(current) || 0) - prev) / Math.abs(prev);
}

const percentText = (change) => {
  const value = Math.round(change * 100);
  return `${value > 0 ? '+' : value < 0 ? MINUS : '±'}${Math.abs(value)} %`;
};
const size = (change) => (change === null ? 0 : Math.abs(change));

// Причина сдвига по цифрам — по самому большому изменению
export function reasonOf(dish, prev) {
  const qty = relativeChange(dish.qty, prev.qty);
  const perPortion = relativeChange(dish.profitPerPortion, prev.profitPerPortion);
  const costPerPortion = dish.qty && prev.qty ? relativeChange(dish.cost / dish.qty, prev.cost / prev.qty) : null;
  let text = '';
  if (size(costPerPortion) >= 0.15 && size(perPortion) >= 0.1 && size(costPerPortion) > size(qty)) {
    // Подорожало в производстве
    text = `себестоимость порции ${percentText(costPerPortion)}, прибыль с порции ${percentText(perPortion)}`;
  } else if (size(qty) >= size(perPortion) && qty !== null && size(qty) >= 0.05) {
    text = `продажи ${percentText(qty)}`;
  } else if (perPortion !== null && size(perPortion) >= 0.05) {
    text = `прибыль с порции ${percentText(perPortion)}${size(qty) < 0.05 ? ', продажи без изменений' : ''}`;
  }
  return { text, qty, profitPerPortion: perPortion, costPerPortion };
}

/**
 * → { changes: Map(key → { direction: up|down|same|new, notable, prevLetter, profitChange, reason }),
 *     gone: [{ key, name, category, prevLetter, revenue, profit }] (по прошлой прибыли, по убыванию),
 *     counts: { down, up, new, gone } — заметные сдвиги, новые и пропавшие }
 */
export function compareAbc(current, previous) {
  const currentDishes = current?.dishes || [];
  const previousDishes = previous?.dishes || [];
  const prevByKey = new Map(previousDishes.map((d) => [d.key, d]));
  const currentKeys = new Set(currentDishes.map((d) => d.key));
  const changes = new Map();
  const counts = { down: 0, up: 0, new: 0, gone: 0 };

  for (const dish of currentDishes) {
    const prev = prevByKey.get(dish.key);
    if (!prev) {
      changes.set(dish.key, { direction: 'new', notable: true, prevLetter: null, profitChange: null, reason: null });
      counts.new += 1;
      continue;
    }
    const step = RANK[dish.abc.profit] - RANK[prev.abc.profit]; // > 0 — хуже (A → B), < 0 — лучше
    const profitChange = relativeChange(dish.profit, prev.profit);
    const direction = step > 0 ? 'down' : step < 0 ? 'up' : 'same';
    const notable = step !== 0 && (Math.abs(step) === 2 || size(profitChange) >= NOTABLE_PROFIT_CHANGE);
    changes.set(dish.key, { direction, notable, prevLetter: prev.abc.profit, profitChange, reason: reasonOf(dish, prev) });
    if (notable) counts[direction] += 1;
  }

  const gone = previousDishes
    .filter((d) => !currentKeys.has(d.key))
    .sort((a, b) => b.profit - a.profit || a.name.localeCompare(b.name, 'ru'))
    .map((d) => ({ key: d.key, name: d.name, category: d.category, prevLetter: d.abc.profit, revenue: d.revenue, profit: d.profit }));
  counts.gone = gone.length;

  return { changes, gone, counts };
}
